import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { MaterialIcons as Icon } from '@expo/vector-icons';
import { useNavigation, useTheme } from '@react-navigation/native';
import { Theme } from '@theme';
import { Header, SearchBar, ListItemSkeletonList, ColumnDivider } from '@components';
import { useScreen, usePaginateColorLabels, ColorLabel } from '@hooks';
import { getCommonStyles } from '@utils';
import EditRelationalColor from './EditRelationalColor';
import RelationalColorDetails from './RelationalColorDetails';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

export const RelationalColors: React.FC = () => {
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const commonStyles = getCommonStyles(theme)
  const { t } = useLanguage();
  const {isWeb, isMobile} = useScreen();
  const styles = getStyles(theme);
  const [searchText, setSearchText] = useState('');
  
  const { flatData: colorLabels, fetchNextPage, hasNextPage, isFetchingNextPage, totalCount, isLoading } = usePaginateColorLabels(searchText);
  
  const [relationalColorId, setRelationalColorId] = useState<string | null>(colorLabels?.[0]?.id || null);
  const [isEdit, setIsEdit] = useState(false);

  // A colour changes id the first time it is renamed or filled (the shared
  // colour's id, then the account's own row id). Its shade never changes and
  // is unique in the palette: the selection follows it, so the same colour
  // stays highlighted after the first save.
  const selected = colorLabels.find((colorLabel) => colorLabel.id === relationalColorId);
  const selectedShade = useRef<string | undefined>(undefined);
  if (selected) selectedShade.current = selected.color;
  const listedIds = colorLabels.map((colorLabel) => colorLabel.id).join(',');

  useEffect(() => {
    if (!relationalColorId || selected || !selectedShade.current) return;
    const moved = colorLabels.find((colorLabel) => colorLabel.color === selectedShade.current);
    if (moved) setRelationalColorId(moved.id);
  }, [listedIds]);

  const navigateToRelationalColorDetails = (relationalColor: ColorLabel) => {
    navigation.navigate('EditRelationalColor', { relationalColorId: relationalColor.id });
  };

  const renderRelationalColorItem = ({ item: relationalColor }: { item: ColorLabel }) => (
    <TouchableOpacity
      testID="relational-color-row"
      style={[styles.relationalColorItem, !isMobile && relationalColorId === relationalColor.id && {backgroundColor: theme.colors.grey10}]}
      onPress={() => {
        if(isMobile) {
          navigateToRelationalColorDetails(relationalColor);
        } else {
          setIsEdit(false);
          setRelationalColorId(relationalColor.id);
        }
      }}
      activeOpacity={0.7}
    >
      <View style={styles.relationalColorContent}>
        <View style={styles.relationalColorInfo}>
          <View style={styles.row}>
            <View style={[styles.color, {backgroundColor: relationalColor.color}]}/>
            <Text style={styles.relationalColorName}>{relationalColor.name}</Text>
          </View>
          <Text style={styles.relationalColorSubtitle}>{t('common.personCount', { count: relationalColor.contactAccountIds?.length || 0 })}</Text>
        </View>
      </View>
      <Icon name="chevron-right" size={24} color="#8E8E93" />
    </TouchableOpacity>
  );

  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={commonStyles.flex}>
        <Header
          title={t('relationalColorsScreen.title')}
          iconLeft={isWeb? undefined : "arrowBack"}
          //iconRight={isMobile? "plus" : undefined}
          //onPressRight={handleAddRelationalColor}
        />
        <View style={[styles.searchContainer, !isMobile && commonStyles.searchContainerMaxWidth]}>
          <SearchBar value={searchText} onChangeText={setSearchText}/>
        </View>

        <Text style={styles.sectionTitle}>{t('common.resultsCount', { count: totalCount })}</Text>
        <View style={[commonStyles.flex, commonStyles.rowGapLg, commonStyles.alignStart]}>
          {isLoading ? (
              <View style={[isMobile? styles.relationalColorsList : commonStyles.flex315, commonStyles.fullHeight]}>
                <ListItemSkeletonList count={8} showColor />
              </View>
          ) : (
            <FlatList
              data={colorLabels}
              renderItem={renderRelationalColorItem}
              keyExtractor={(item) => item.id}
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.5}
              showsVerticalScrollIndicator={false}
              style={[isMobile? styles.relationalColorsList : commonStyles.flex315, commonStyles.fullHeight]}
            />
          )}
          {!isMobile && <ColumnDivider />}
          {!isMobile && !!relationalColorId && !isEdit && <View style={[commonStyles.flex, commonStyles.fullHeight]}>
            <RelationalColorDetails relationalColorId={relationalColorId} onEdit={() => setIsEdit(true)} />
          </View>}
          {!isMobile && !!relationalColorId && isEdit && <View style={[commonStyles.flex, commonStyles.fullHeight]}>
            <EditRelationalColor relationalColorId={relationalColorId} onBack={() => setIsEdit(false)} />
          </View>}
        </View>
      </View>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#fff',
  },
  searchContainer: {
    marginTop: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: '#fff',
  },
  buttonAdd: {
    backgroundColor: theme.colors.white,
    marginRight: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: 13,
    color: '#8E8E93',
    fontWeight: '400',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  relationalColorsList: {
    flex: 1,
  },
  relationalColorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E5EA',
  },
  relationalColorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  relationalColorIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    backgroundColor: '#007AFF',
  },
  relationalColorSymbol: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  relationalColorInfo: {
    flex: 1,
  },
  color: {
    width: 25, height: 25, borderRadius: 13,
    // Keeps light pastel dots visible on the white background.
    borderWidth: 1, borderColor: 'rgba(0, 0, 0, 0.12)',
  },
  relationalColorName: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000',
  },
  relationalColorSubtitle: {
    fontSize: 15,
    color: '#8E8E93',
    fontWeight: '400',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  }
}); 